import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Palette, 
  Image,
  Type,
  Layers,
  Droplet,
  Layout,
  ArrowLeft,
  RefreshCw,
  Copy,
  Download,
  Sparkles,
  Wand2,
  Lock,
  Crown,
  Plus,
  Upload,
  Paintbrush,
  Eraser,
  ChevronRight,
  Home
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import useUser from "@/hooks/useUser";
import { PremiumLock, LoginRequired } from "@/components/PremiumLock";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";
import WhatsAppChat from "@/components/WhatsAppChat";
import RelatedTools from "@/components/RelatedTools";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function DesignTools() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, plan, isPremium, isLoggedIn, loading: userLoading } = useUser();
  const [activeTool, setActiveTool] = useState("color-palette");

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Color Palette State
  const [baseColor, setBaseColor] = useState("#6366f1");
  const [paletteType, setPaletteType] = useState("complementary");
  const [generatedPalette, setGeneratedPalette] = useState(null);

  // Logo Maker State
  const [brandName, setBrandName] = useState("");
  const [industry, setIndustry] = useState("technology");
  const [logoStyle, setLogoStyle] = useState("modern");
  const [logoColors, setLogoColors] = useState("");
  const [logoDescription, setLogoDescription] = useState("");
  const [generatedLogo, setGeneratedLogo] = useState(null);
  const [loadingLogo, setLoadingLogo] = useState(false);

  // Banner Creator State
  const [bannerHeadline, setBannerHeadline] = useState("");
  const [bannerSubheadline, setBannerSubheadline] = useState("");
  const [bannerCTA, setBannerCTA] = useState("");
  const [bannerType, setBannerType] = useState("website");
  const [bannerSize, setBannerSize] = useState("hero");
  const [bannerStyle, setBannerStyle] = useState("modern");
  const [generatedBanner, setGeneratedBanner] = useState(null);
  const [loadingBanner, setLoadingBanner] = useState(false);

  // Thumbnail Designer State
  const [videoTitle, setVideoTitle] = useState("");
  const [thumbnailHeadline, setThumbnailHeadline] = useState("");
  const [thumbnailStyle, setThumbnailStyle] = useState("bold");
  const [thumbnailColor, setThumbnailColor] = useState("red");
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const [loadingThumbnail, setLoadingThumbnail] = useState(false);
  // Pro features
  const [thumbnailPrompt, setThumbnailPrompt] = useState("");
  const [useGradient, setUseGradient] = useState(false);
  const [gradientStart, setGradientStart] = useState("#FF0000");
  const [gradientEnd, setGradientEnd] = useState("#FF6600");
  const [referenceImage, setReferenceImage] = useState(null);
  const [thumbnailTab, setThumbnailTab] = useState("basic");

  // Background Remover State
  const [bgRemoverImage, setBgRemoverImage] = useState(null);
  const [bgRemoverPreview, setBgRemoverPreview] = useState(null);
  const [bgRemoverResult, setBgRemoverResult] = useState(null);
  const [loadingBgRemover, setLoadingBgRemover] = useState(false);
  const [bgColor, setBgColor] = useState("transparent");

  // Handle Background Removal
  const handleBgRemoverUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBgRemoverImage(file);
      setBgRemoverResult(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBgRemoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveBackground = async () => {
    if (!bgRemoverImage) {
      toast.error("Please upload an image first");
      return;
    }

    setLoadingBgRemover(true);
    try {
      const formData = new FormData();
      formData.append('image', bgRemoverImage);
      formData.append('bg_color', bgColor);

      const response = await axios.post(`${API}/design/remove-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      setBgRemoverResult(response.data);
      toast.success("Background removed successfully!");
    } catch (error) {
      // Fallback: Show demo result with transparency effect
      toast.info("Using demo mode - actual API coming soon!");
      setBgRemoverResult({
        success: true,
        demo: true,
        original_image: bgRemoverPreview,
        message: "Background removal preview (demo mode)"
      });
    } finally {
      setLoadingBgRemover(false);
    }
  };

  const downloadBgRemovedImage = (format = 'png') => {
    if (!bgRemoverResult) return;
    
    if (bgRemoverResult.image_base64) {
      const link = document.createElement('a');
      link.href = `data:image/${format};base64,${bgRemoverResult.image_base64}`;
      link.download = `bg_removed_${Date.now()}.${format}`;
      link.click();
      toast.success(`Image downloaded as ${format.toUpperCase()}`);
    } else {
      toast.info("Demo mode - download not available");
    }
  };

  // QR Code Generator State
  const [qrContent, setQrContent] = useState("");
  const [qrType, setQrType] = useState("url");
  const [qrColor, setQrColor] = useState("#000000");
  const [qrBgColor, setQrBgColor] = useState("#FFFFFF");
  const [generatedQr, setGeneratedQr] = useState(null);

  // Mockup Generator State
  const [mockupType, setMockupType] = useState("phone");
  const [mockupImage, setMockupImage] = useState(null);
  const [mockupPreview, setMockupPreview] = useState(null);
  const [generatedMockup, setGeneratedMockup] = useState(null);
  const [loadingMockup, setLoadingMockup] = useState(false);

  // QR Code Generator (using canvas)
  const generateQrCode = () => {
    if (!qrContent.trim()) {
      toast.error("Please enter content for QR code");
      return;
    }
    
    // Create simple QR pattern simulation (visual demo)
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    // Background
    ctx.fillStyle = qrBgColor;
    ctx.fillRect(0, 0, 200, 200);
    
    // Generate pattern based on content hash
    ctx.fillStyle = qrColor;
    const hash = qrContent.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    
    // Draw QR-like pattern
    for (let i = 0; i < 21; i++) {
      for (let j = 0; j < 21; j++) {
        // Corner patterns
        if ((i < 7 && j < 7) || (i < 7 && j > 13) || (i > 13 && j < 7)) {
          if ((i === 0 || i === 6 || j === 0 || j === 6 || j === 14 || j === 20 || i === 14 || i === 20) ||
              (i >= 2 && i <= 4 && j >= 2 && j <= 4) ||
              (i >= 2 && i <= 4 && j >= 16 && j <= 18) ||
              (i >= 16 && i <= 18 && j >= 2 && j <= 4)) {
            ctx.fillRect(j * 8 + 16, i * 8 + 16, 6, 6);
          }
        } else {
          // Random pattern based on content
          if ((hash + i * j) % 3 === 0) {
            ctx.fillRect(j * 8 + 16, i * 8 + 16, 6, 6);
          }
        }
      }
    }
    
    setGeneratedQr({
      dataUrl: canvas.toDataURL('image/png'),
      content: qrContent,
      type: qrType
    });
    toast.success("QR Code generated!");
  };

  // Download QR Code
  const downloadQrCode = () => {
    if (!generatedQr) return;
    const link = document.createElement('a');
    link.href = generatedQr.dataUrl;
    link.download = `qr_code_${Date.now()}.png`;
    link.click();
    toast.success("QR Code downloaded!");
  };

  // Mockup Generator Handler
  const handleGenerateMockup = () => {
    if (!mockupPreview) {
      toast.error("Please upload an image first");
      return;
    }
    setLoadingMockup(true);
    
    setTimeout(() => {
      // Simulate mockup generation
      setGeneratedMockup({
        type: mockupType,
        preview: mockupPreview,
        generated: true
      });
      setLoadingMockup(false);
      toast.success("Mockup generated!");
    }, 800);
  };

  const handleMockupImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMockupImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setMockupPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const designTools = [
    {
      id: "color-palette",
      name: "Color Palette Generator",
      icon: <Droplet className="w-5 h-5" />,
      description: "Generate harmonious color palettes",
      premium: false
    },
    {
      id: "logo-maker",
      name: "Logo Maker",
      icon: <Image className="w-5 h-5" />,
      description: "Create professional logos with AI",
      premium: true
    },
    {
      id: "social-templates",
      name: "Social Media Templates",
      icon: <Layout className="w-5 h-5" />,
      description: "Ready-to-use social templates",
      premium: false
    },
    {
      id: "banner-creator",
      name: "Banner Creator",
      icon: <Layers className="w-5 h-5" />,
      description: "Design eye-catching banners",
      premium: false
    },
    {
      id: "thumbnail-designer",
      name: "Thumbnail Designer",
      icon: <Image className="w-5 h-5" />,
      description: "Create YouTube thumbnails",
      premium: false
    },
    {
      id: "bg-remover",
      name: "Background Remover",
      icon: <Eraser className="w-5 h-5" />,
      description: "Remove image backgrounds instantly",
      premium: false,
      badge: "NEW"
    },
    {
      id: "qr-generator",
      name: "QR Code Generator",
      icon: <Layout className="w-5 h-5" />,
      description: "Create custom QR codes",
      premium: false,
      badge: "NEW"
    },
    {
      id: "mockup-generator",
      name: "Mockup Generator",
      icon: <Layers className="w-5 h-5" />,
      description: "Product mockups in seconds",
      premium: false,
      badge: "NEW"
    },
    {
      id: "brand-kit",
      name: "Brand Kit Manager",
      icon: <Palette className="w-5 h-5" />,
      description: "Manage your brand assets",
      premium: true
    },
    {
      id: "pdf-tools",
      name: "PDF Tools",
      icon: <Type className="w-5 h-5" />,
      description: "40+ PDF conversion & editing tools",
      premium: false,
      isLink: true,
      linkTo: "/tools/design/pdf"
    }
  ];

  // Color utility functions
  const hexToHSL = (hex) => {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  };

  const hslToHex = (h, s, l) => {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = x => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const generatePalette = () => {
    const hsl = hexToHSL(baseColor);
    let colors = [];

    switch (paletteType) {
      case 'complementary':
        colors = [
          { hex: baseColor, name: 'Primary' },
          { hex: hslToHex((hsl.h + 180) % 360, hsl.s, hsl.l), name: 'Complementary' },
          { hex: hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 20, 95)), name: 'Light' },
          { hex: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 20, 10)), name: 'Dark' },
          { hex: hslToHex((hsl.h + 180) % 360, hsl.s, Math.min(hsl.l + 20, 95)), name: 'Accent Light' }
        ];
        break;
      case 'analogous':
        colors = [
          { hex: hslToHex((hsl.h - 30 + 360) % 360, hsl.s, hsl.l), name: 'Analogous 1' },
          { hex: baseColor, name: 'Primary' },
          { hex: hslToHex((hsl.h + 30) % 360, hsl.s, hsl.l), name: 'Analogous 2' },
          { hex: hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 25, 95)), name: 'Light' },
          { hex: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 25, 10)), name: 'Dark' }
        ];
        break;
      case 'triadic':
        colors = [
          { hex: baseColor, name: 'Primary' },
          { hex: hslToHex((hsl.h + 120) % 360, hsl.s, hsl.l), name: 'Triadic 1' },
          { hex: hslToHex((hsl.h + 240) % 360, hsl.s, hsl.l), name: 'Triadic 2' },
          { hex: hslToHex(hsl.h, hsl.s * 0.5, hsl.l), name: 'Muted' },
          { hex: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 30, 10)), name: 'Dark' }
        ];
        break;
      case 'monochromatic':
        colors = [
          { hex: hslToHex(hsl.h, hsl.s, 95), name: 'Lightest' },
          { hex: hslToHex(hsl.h, hsl.s, 75), name: 'Light' },
          { hex: baseColor, name: 'Primary' },
          { hex: hslToHex(hsl.h, hsl.s, 35), name: 'Dark' },
          { hex: hslToHex(hsl.h, hsl.s, 15), name: 'Darkest' }
        ];
        break;
      default:
        colors = [{ hex: baseColor, name: 'Primary' }];
    }

    setGeneratedPalette({
      baseColor,
      type: paletteType,
      colors
    });
    toast.success("Palette generated!");
  };

  // Logo Maker Functions
  const handleGenerateLogo = async () => {
    if (!brandName.trim()) {
      toast.error("Please enter a brand name");
      return;
    }

    setLoadingLogo(true);
    try {
      const colors = logoColors.split(',').map(c => c.trim()).filter(c => c);
      
      const response = await axios.post(`${API}/design/generate-logo`, {
        brand_name: brandName.trim(),
        industry: industry,
        style: logoStyle,
        colors: colors.length > 0 ? colors : null,
        description: logoDescription.trim() || null
      });
      
      setGeneratedLogo(response.data);
      toast.success("Logo generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate logo");
    } finally {
      setLoadingLogo(false);
    }
  };

  // Logo Download with Multiple Formats
  const downloadLogo = (format = 'png') => {
    if (!generatedLogo?.image_base64) return;
    
    const link = document.createElement('a');
    const fileName = `${generatedLogo.brand_name.replace(/\s+/g, '_')}_logo`;
    
    if (format === 'png') {
      link.href = `data:image/png;base64,${generatedLogo.image_base64}`;
      link.download = `${fileName}.png`;
    } else if (format === 'jpg' || format === 'jpeg') {
      // Convert PNG to JPEG using canvas
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const jpgUrl = canvas.toDataURL('image/jpeg', 0.92);
        const jpgLink = document.createElement('a');
        jpgLink.href = jpgUrl;
        jpgLink.download = `${fileName}.jpg`;
        jpgLink.click();
        toast.success("Logo downloaded as JPG!");
      };
      img.src = `data:image/png;base64,${generatedLogo.image_base64}`;
      return;
    } else if (format === 'svg') {
      // Create an SVG wrapper around the PNG (embedded image)
      const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="1024" height="1024" viewBox="0 0 1024 1024">
  <title>${generatedLogo.brand_name} Logo</title>
  <image width="1024" height="1024" xlink:href="data:image/png;base64,${generatedLogo.image_base64}"/>
</svg>`;
      const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.svg`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Logo downloaded as SVG!");
      return;
    }
    
    link.click();
    toast.success(`Logo downloaded as ${format.toUpperCase()}!`);
  };

  // Banner Generator Function
  const handleGenerateBanner = async () => {
    if (!bannerHeadline.trim()) {
      toast.error("Please enter a headline");
      return;
    }

    setLoadingBanner(true);
    try {
      const response = await axios.post(`${API}/design/generate-banner`, {
        headline: bannerHeadline.trim(),
        subheadline: bannerSubheadline.trim() || null,
        call_to_action: bannerCTA.trim() || null,
        banner_type: bannerType,
        size_preset: bannerSize,
        style: bannerStyle,
        colors: null
      });
      
      setGeneratedBanner(response.data);
      toast.success("Banner generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate banner");
    } finally {
      setLoadingBanner(false);
    }
  };

  // Banner Download with Multiple Formats
  const downloadBanner = (format = 'png') => {
    if (!generatedBanner?.image_base64) return;
    
    const link = document.createElement('a');
    const fileName = `banner_${bannerType}_${Date.now()}`;
    
    if (format === 'png') {
      link.href = `data:image/png;base64,${generatedBanner.image_base64}`;
      link.download = `${fileName}.png`;
      link.click();
      toast.success("Banner downloaded as PNG!");
    } else if (format === 'jpg' || format === 'jpeg') {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const jpgUrl = canvas.toDataURL('image/jpeg', 0.92);
        const jpgLink = document.createElement('a');
        jpgLink.href = jpgUrl;
        jpgLink.download = `${fileName}.jpg`;
        jpgLink.click();
        toast.success("Banner downloaded as JPG!");
      };
      img.src = `data:image/png;base64,${generatedBanner.image_base64}`;
    }
  };

  // Thumbnail Download with Multiple Formats
  const downloadThumbnail = (format = 'png') => {
    if (!generatedThumbnail?.image_base64) return;
    
    const link = document.createElement('a');
    const fileName = `thumbnail_${Date.now()}`;
    
    if (format === 'png') {
      link.href = `data:image/png;base64,${generatedThumbnail.image_base64}`;
      link.download = `${fileName}.png`;
      link.click();
      toast.success("Thumbnail downloaded as PNG!");
    } else if (format === 'jpg' || format === 'jpeg') {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const jpgUrl = canvas.toDataURL('image/jpeg', 0.92);
        const jpgLink = document.createElement('a');
        jpgLink.href = jpgUrl;
        jpgLink.download = `${fileName}.jpg`;
        jpgLink.click();
        toast.success("Thumbnail downloaded as JPG!");
      };
      img.src = `data:image/png;base64,${generatedThumbnail.image_base64}`;
    }
  };

  // Thumbnail Generator Function
  const handleGenerateThumbnail = async () => {
    // Basic mode needs video title, Pro mode can use custom prompt
    if (thumbnailTab === "basic" && !videoTitle.trim()) {
      toast.error("Please enter a video title");
      return;
    }
    if (thumbnailTab === "pro" && !thumbnailPrompt.trim() && !videoTitle.trim()) {
      toast.error("Please enter a custom prompt or video title");
      return;
    }

    setLoadingThumbnail(true);
    try {
      const requestData = {
        video_title: videoTitle.trim() || thumbnailPrompt.trim(),
        headline_text: thumbnailHeadline.trim() || null,
        style: thumbnailStyle,
        color_scheme: useGradient ? `gradient:${gradientStart}-${gradientEnd}` : thumbnailColor,
        include_text: true,
        // Pro features
        custom_prompt: thumbnailTab === "pro" ? thumbnailPrompt.trim() : null,
        use_gradient: useGradient,
        gradient_colors: useGradient ? [gradientStart, gradientEnd] : null
      };
      
      const response = await axios.post(`${API}/design/generate-thumbnail`, requestData);
      
      setGeneratedThumbnail(response.data);
      toast.success("Thumbnail generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate thumbnail");
    } finally {
      setLoadingThumbnail(false);
    }
  };

  // Handle reference image upload
  const handleReferenceImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setReferenceImage(e.target.result);
        toast.success("Reference image uploaded!");
      };
      reader.readAsDataURL(file);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const copyAllColors = () => {
    const colorCodes = generatedPalette.colors.map(c => `${c.name}: ${c.hex}`).join('\n');
    navigator.clipboard.writeText(colorCodes);
    toast.success("All colors copied!");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-4" aria-label="Breadcrumb">
          <RouterLink to="/" className="flex items-center gap-1 text-gray-500 hover:text-orange-600 transition-colors">
            <Home className="w-4 h-4" />
            <span>Home</span>
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <RouterLink to="/tools" className="text-gray-500 hover:text-orange-600 transition-colors">
            Tools
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">Design Tools</span>
        </nav>

        {/* Page Title — Premium */}
        <div className="mb-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#111111] rounded-2xl flex items-center justify-center">
              <Palette className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">Design Suite</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#111111]" data-testid="page-title">Design Tools</h1>
              <p className="text-slate-600 mt-1.5">Create stunning visuals for your brand and marketing</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Design Tools</h3>
              <div className="space-y-2">
                {designTools.map((tool) => (
                  tool.isLink ? (
                    <button
                      key={tool.id}
                      onClick={() => navigate(tool.linkTo)}
                      data-testid={`tool-${tool.id}`}
                      className="w-full text-left px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 text-orange-700 border border-orange-200"
                    >
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <div>
                          <span className="text-sm font-medium">{tool.name}</span>
                          <p className="text-xs text-orange-500">{tool.description}</p>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTool(tool.id)}
                      data-testid={`tool-${tool.id}`}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        activeTool === tool.id
                          ? 'bg-orange-100 text-orange-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {tool.icon}
                          <span className="text-sm">{tool.name}</span>
                        </div>
                        {tool.premium && !isPremium && (
                          <Lock className="w-4 h-4 text-violet-500" />
                        )}
                        {tool.premium && isPremium && (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                      </div>
                    </button>
                  )
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Color Palette Generator */}
            {activeTool === "color-palette" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Droplet className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Color Palette Generator</h2>
                    <p className="text-gray-600">Create harmonious color schemes for your brand</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Base Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={baseColor}
                        onChange={(e) => setBaseColor(e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        data-testid="color-picker"
                      />
                      <Input
                        type="text"
                        value={baseColor}
                        onChange={(e) => setBaseColor(e.target.value)}
                        className="flex-1"
                        data-testid="color-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Palette Type</label>
                    <Select value={paletteType} onValueChange={setPaletteType}>
                      <SelectTrigger data-testid="palette-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="complementary">Complementary</SelectItem>
                        <SelectItem value="analogous">Analogous</SelectItem>
                        <SelectItem value="triadic">Triadic</SelectItem>
                        <SelectItem value="monochromatic">Monochromatic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={generatePalette}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      data-testid="generate-palette-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </Button>
                  </div>
                </div>

                {generatedPalette && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Your {generatedPalette.type} Palette</h3>
                      <Button variant="outline" size="sm" onClick={copyAllColors}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy All
                      </Button>
                    </div>

                    <div className="grid grid-cols-5 gap-4">
                      {generatedPalette.colors.map((color, idx) => (
                        <div key={idx} className="text-center">
                          <div 
                            className="h-24 rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform"
                            style={{ backgroundColor: color.hex }}
                            onClick={() => copyToClipboard(color.hex)}
                            title="Click to copy"
                          />
                          <p className="mt-2 text-sm font-medium">{color.name}</p>
                          <p className="text-xs text-gray-500 uppercase">{color.hex}</p>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">CSS Variables</h4>
                      <pre className="text-sm bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`:root {
${generatedPalette.colors.map(c => `  --color-${c.name.toLowerCase().replace(' ', '-')}: ${c.hex};`).join('\n')}
}`}
                      </pre>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Logo Maker */}
            {activeTool === "logo-maker" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Wand2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-gray-900">AI Logo Maker</h2>
                      <Badge className="bg-violet-100 text-violet-700">
                        <Crown className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    </div>
                    <p className="text-gray-600">Generate professional logos with AI</p>
                  </div>
                </div>

                {/* Check if user is logged in */}
                {!isLoggedIn && !userLoading && (
                  <LoginRequired message="Please login to use the AI Logo Maker." />
                )}

                {/* Check if user has premium access */}
                {isLoggedIn && !isPremium && (
                  <PremiumLock 
                    feature="AI Logo Maker"
                    description="Generate stunning, professional logos with AI. Upgrade to Pro to unlock this feature and create unlimited logos for your brand."
                    showCard={false}
                  />
                )}

                {/* Show tool if user has access */}
                {isLoggedIn && isPremium && (
                  <>
                    <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Brand Name *</label>
                    <Input
                      placeholder="e.g., TechFlow, GreenLeaf, Luxuria"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      data-testid="brand-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Industry</label>
                      <Select value={industry} onValueChange={setIndustry}>
                        <SelectTrigger data-testid="industry-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technology">Technology</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="finance">Finance</SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="food">Food & Restaurant</SelectItem>
                          <SelectItem value="fashion">Fashion & Beauty</SelectItem>
                          <SelectItem value="fitness">Fitness & Sports</SelectItem>
                          <SelectItem value="real-estate">Real Estate</SelectItem>
                          <SelectItem value="entertainment">Entertainment</SelectItem>
                          <SelectItem value="general">General/Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Style</label>
                      <Select value={logoStyle} onValueChange={setLogoStyle}>
                        <SelectTrigger data-testid="style-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="modern">Modern & Clean</SelectItem>
                          <SelectItem value="minimalist">Minimalist</SelectItem>
                          <SelectItem value="vintage">Vintage & Retro</SelectItem>
                          <SelectItem value="playful">Playful & Fun</SelectItem>
                          <SelectItem value="corporate">Corporate & Professional</SelectItem>
                          <SelectItem value="luxury">Luxury & Elegant</SelectItem>
                          <SelectItem value="tech">Tech & Futuristic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Colors (optional)</label>
                    <Input
                      placeholder="e.g., blue, gold, white (comma separated)"
                      value={logoColors}
                      onChange={(e) => setLogoColors(e.target.value)}
                      data-testid="colors-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description (optional)</label>
                    <Textarea
                      placeholder="Describe your brand and what makes it unique..."
                      value={logoDescription}
                      onChange={(e) => setLogoDescription(e.target.value)}
                      className="min-h-[80px]"
                      data-testid="description-input"
                    />
                  </div>

                  <Button 
                    onClick={handleGenerateLogo}
                    disabled={loadingLogo}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="generate-logo-btn"
                  >
                    {loadingLogo ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Logo (30-60s)...</>
                    ) : (
                      <><Wand2 className="w-4 h-4 mr-2" /> Generate Logo</>
                    )}
                  </Button>
                </div>

                {generatedLogo && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h3 className="font-semibold">Generated Logo for "{generatedLogo.brand_name}"</h3>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadLogo('png')}
                          data-testid="download-logo-png-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          PNG
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadLogo('jpg')}
                          data-testid="download-logo-jpg-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          JPG
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadLogo('svg')}
                          data-testid="download-logo-svg-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          SVG
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-center p-8 bg-gray-100 rounded-lg">
                      <img 
                        src={`data:image/png;base64,${generatedLogo.image_base64}`}
                        alt={`${generatedLogo.brand_name} logo`}
                        className="max-w-md max-h-96 object-contain rounded-lg shadow-lg"
                        data-testid="generated-logo-image"
                      />
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium mb-2">Logo Details</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <p><span className="text-gray-600">Brand:</span> {generatedLogo.brand_name}</p>
                        <p><span className="text-gray-600">Style:</span> {generatedLogo.style}</p>
                      </div>
                    </div>
                  </div>
                )}
                  </>
                )}
              </Card>
            )}

            {/* Social Media Templates */}
            {activeTool === "social-templates" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-red-500 rounded-lg flex items-center justify-center">
                    <Layout className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Social Media Templates</h2>
                    <p className="text-gray-600">Ready-to-use templates for all platforms</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { platform: "Instagram Post", size: "1080x1080", bg: "from-purple-500 to-pink-500" },
                    { platform: "Instagram Story", size: "1080x1920", bg: "from-pink-500 to-orange-500" },
                    { platform: "Facebook Post", size: "1200x630", bg: "from-blue-500 to-blue-700" },
                    { platform: "Twitter Post", size: "1200x675", bg: "from-sky-400 to-blue-500" },
                    { platform: "LinkedIn Banner", size: "1584x396", bg: "from-blue-700 to-blue-900" },
                    { platform: "YouTube Thumbnail", size: "1280x720", bg: "from-red-500 to-red-700" },
                  ].map((template, idx) => (
                    <div key={idx} className="group cursor-pointer">
                      <div className={`h-40 bg-gradient-to-br ${template.bg} rounded-lg flex items-center justify-center mb-3 group-hover:scale-105 transition-transform shadow-md`}>
                        <span className="text-white font-semibold">{template.size}</span>
                      </div>
                      <h4 className="font-medium">{template.platform}</h4>
                      <p className="text-sm text-gray-500">{template.size}px</p>
                      <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => toast.success(`${template.platform} template copied!`)}>
                        Use Template
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Quick Tips</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Instagram prefers 1:1 square images for feed posts</li>
                    <li>• Use 9:16 ratio for Stories and Reels</li>
                    <li>• Facebook engagement is higher with 1200x630 images</li>
                    <li>• Keep important content away from edges (safe zone)</li>
                  </ul>
                </div>
              </Card>
            )}

            {/* Banner Creator */}
            {activeTool === "banner-creator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                    <Layers className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">AI Banner Creator</h2>
                    <p className="text-gray-600">Generate eye-catching banners with AI</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Banner Type</label>
                      <Select value={bannerType} onValueChange={setBannerType}>
                        <SelectTrigger data-testid="banner-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="website">Website Hero Banner</SelectItem>
                          <SelectItem value="ad">Display Ad Banner</SelectItem>
                          <SelectItem value="email">Email Header</SelectItem>
                          <SelectItem value="social">Social Cover</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Size Preset</label>
                      <Select value={bannerSize} onValueChange={setBannerSize}>
                        <SelectTrigger data-testid="banner-size-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hero">Hero Banner (1920x600)</SelectItem>
                          <SelectItem value="leaderboard">Leaderboard (728x90)</SelectItem>
                          <SelectItem value="rectangle">Medium Rectangle (300x250)</SelectItem>
                          <SelectItem value="skyscraper">Wide Skyscraper (160x600)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Style</label>
                    <Select value={bannerStyle} onValueChange={setBannerStyle}>
                      <SelectTrigger data-testid="banner-style-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="modern">Modern & Clean</SelectItem>
                        <SelectItem value="corporate">Corporate & Professional</SelectItem>
                        <SelectItem value="playful">Playful & Colorful</SelectItem>
                        <SelectItem value="elegant">Elegant & Luxury</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Headline Text *</label>
                    <Input 
                      placeholder="Enter your banner headline..."
                      value={bannerHeadline}
                      onChange={(e) => setBannerHeadline(e.target.value)}
                      data-testid="banner-headline-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subheadline (optional)</label>
                    <Input 
                      placeholder="Supporting text or description..."
                      value={bannerSubheadline}
                      onChange={(e) => setBannerSubheadline(e.target.value)}
                      data-testid="banner-subheadline-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Call to Action (optional)</label>
                    <Input 
                      placeholder="e.g., Shop Now, Learn More, Sign Up"
                      value={bannerCTA}
                      onChange={(e) => setBannerCTA(e.target.value)}
                      data-testid="banner-cta-input"
                    />
                  </div>

                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={handleGenerateBanner}
                    disabled={loadingBanner}
                    data-testid="generate-banner-btn"
                  >
                    {loadingBanner ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Banner (30-60s)...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate Banner with AI</>
                    )}
                  </Button>

                  {/* Generated Banner */}
                  {generatedBanner ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="font-semibold">Generated Banner</h3>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadBanner('png')}
                            data-testid="download-banner-png-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            PNG
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadBanner('jpg')}
                            data-testid="download-banner-jpg-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            JPG
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-center p-4 bg-gray-100 rounded-lg">
                        <img 
                          src={`data:image/png;base64,${generatedBanner.image_base64}`}
                          alt="Generated banner"
                          className="max-w-full max-h-96 object-contain rounded-lg shadow-lg"
                          data-testid="generated-banner-image"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                      <Layers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">Banner preview will appear here</p>
                      <p className="text-sm text-gray-400">Fill in the details and click Generate</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Thumbnail Designer */}
            {activeTool === "thumbnail-designer" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-lg flex items-center justify-center">
                    <Image className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">AI Thumbnail Designer</h2>
                    <p className="text-gray-600">Create click-worthy YouTube thumbnails with AI</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Tabs for Basic vs Pro Mode */}
                  <Tabs value={thumbnailTab} onValueChange={setThumbnailTab}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="basic">Basic Mode</TabsTrigger>
                      <TabsTrigger value="pro" className="flex items-center gap-2">
                        <Crown className="w-3 h-3" /> Pro Mode
                      </TabsTrigger>
                    </TabsList>

                    {/* Basic Mode */}
                    <TabsContent value="basic" className="space-y-4 mt-4">
                      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <h4 className="font-medium text-yellow-800 mb-2">YouTube Thumbnail Best Practices</h4>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          <li>• Use 1280x720 resolution (16:9 ratio)</li>
                          <li>• Include faces with emotions for 38% higher CTR</li>
                          <li>• Use contrasting colors and bold text</li>
                          <li>• Keep text to 3-5 words maximum</li>
                        </ul>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Video Title *</label>
                          <Input 
                            placeholder="What's your video about?"
                            value={videoTitle}
                            onChange={(e) => setVideoTitle(e.target.value)}
                            data-testid="video-title-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Thumbnail Style</label>
                          <Select value={thumbnailStyle} onValueChange={setThumbnailStyle}>
                            <SelectTrigger data-testid="thumbnail-style-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bold">Bold & Colorful</SelectItem>
                              <SelectItem value="minimal">Minimalist</SelectItem>
                              <SelectItem value="face">Face Close-up</SelectItem>
                              <SelectItem value="text">Text Focused</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Headline Text (3-5 words, optional)</label>
                        <Input 
                          placeholder="e.g., WATCH THIS NOW!"
                          value={thumbnailHeadline}
                          onChange={(e) => setThumbnailHeadline(e.target.value)}
                          data-testid="thumbnail-headline-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Choose Color Scheme</label>
                        <div className="flex gap-3 flex-wrap">
                          {[
                            { name: "YouTube Red", color: "bg-red-600", value: "red" },
                            { name: "Electric Blue", color: "bg-blue-500", value: "blue" },
                            { name: "Neon Green", color: "bg-green-400", value: "green" },
                            { name: "Orange Pop", color: "bg-orange-500", value: "orange" },
                            { name: "Purple Haze", color: "bg-purple-600", value: "purple" },
                            { name: "Gold Rush", color: "bg-yellow-500", value: "gold" }
                          ].map((scheme, idx) => (
                            <div 
                              key={idx} 
                              className={`text-center cursor-pointer group ${thumbnailColor === scheme.value ? 'ring-2 ring-offset-2 ring-violet-500 rounded-lg' : ''}`}
                              onClick={() => { setThumbnailColor(scheme.value); setUseGradient(false); }}
                              data-testid={`color-scheme-${scheme.value}`}
                            >
                              <div className={`w-12 h-12 ${scheme.color} rounded-lg shadow-md group-hover:scale-110 transition-transform`} />
                              <p className="text-xs mt-1 text-gray-600">{scheme.name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>

                    {/* Pro Mode */}
                    <TabsContent value="pro" className="space-y-4 mt-4">
                      <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Crown className="w-4 h-4 text-violet-600" />
                          <h4 className="font-medium text-violet-800">Pro Features</h4>
                        </div>
                        <p className="text-sm text-violet-700">
                          Custom AI prompts, gradient colors, reference images, and advanced styling options.
                        </p>
                      </div>

                      {/* Custom AI Prompt */}
                      <div>
                        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                          <Wand2 className="w-4 h-4 text-violet-500" />
                          Custom AI Prompt
                        </label>
                        <Textarea
                          placeholder="Describe your perfect thumbnail... e.g., 'A dramatic explosion with a shocked face reaction, bright orange flames, bold white text saying UNBELIEVABLE'"
                          value={thumbnailPrompt}
                          onChange={(e) => setThumbnailPrompt(e.target.value)}
                          className="h-24"
                          data-testid="custom-prompt-input"
                        />
                      </div>

                      {/* Reference Image Upload */}
                      <div>
                        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                          <Upload className="w-4 h-4 text-violet-500" />
                          Reference Image (Optional)
                        </label>
                        <div className="flex items-center gap-4">
                          <label className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-violet-400 cursor-pointer transition-colors text-center">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleReferenceImageUpload}
                              className="hidden"
                              data-testid="reference-image-input"
                            />
                            {referenceImage ? (
                              <div className="flex items-center justify-center gap-2">
                                <img src={referenceImage} alt="Reference" className="w-16 h-16 object-cover rounded" />
                                <span className="text-sm text-green-600">Image uploaded</span>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                <Upload className="w-8 h-8 mx-auto mb-2" />
                                <p className="text-sm">Click to upload reference image</p>
                              </div>
                            )}
                          </label>
                          {referenceImage && (
                            <Button variant="outline" size="sm" onClick={() => setReferenceImage(null)}>
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Gradient Color Picker */}
                      <div>
                        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                          <Paintbrush className="w-4 h-4 text-violet-500" />
                          Gradient Colors
                        </label>
                        <div className="flex items-center gap-3 mb-3">
                          <input
                            type="checkbox"
                            checked={useGradient}
                            onChange={(e) => setUseGradient(e.target.checked)}
                            className="w-4 h-4 accent-violet-600"
                            data-testid="use-gradient-checkbox"
                          />
                          <span className="text-sm">Use gradient background</span>
                        </div>
                        {useGradient && (
                          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <label className="text-xs text-gray-500 mb-1 block">Start Color</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={gradientStart}
                                  onChange={(e) => setGradientStart(e.target.value)}
                                  className="w-10 h-10 rounded cursor-pointer"
                                  data-testid="gradient-start-color"
                                />
                                <Input
                                  value={gradientStart}
                                  onChange={(e) => setGradientStart(e.target.value)}
                                  className="w-24 text-sm"
                                />
                              </div>
                            </div>
                            <div className="text-2xl text-gray-400">→</div>
                            <div className="flex-1">
                              <label className="text-xs text-gray-500 mb-1 block">End Color</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={gradientEnd}
                                  onChange={(e) => setGradientEnd(e.target.value)}
                                  className="w-10 h-10 rounded cursor-pointer"
                                  data-testid="gradient-end-color"
                                />
                                <Input
                                  value={gradientEnd}
                                  onChange={(e) => setGradientEnd(e.target.value)}
                                  className="w-24 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {useGradient && (
                          <div 
                            className="mt-3 h-12 rounded-lg shadow-inner"
                            style={{ background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})` }}
                          />
                        )}
                      </div>

                      {/* Quick Gradient Presets */}
                      {useGradient && (
                        <div>
                          <label className="text-xs text-gray-500 mb-2 block">Quick Presets</label>
                          <div className="flex gap-2 flex-wrap">
                            {[
                              { name: "Sunset", start: "#FF512F", end: "#DD2476" },
                              { name: "Ocean", start: "#2193b0", end: "#6dd5ed" },
                              { name: "Forest", start: "#11998e", end: "#38ef7d" },
                              { name: "Fire", start: "#f12711", end: "#f5af19" },
                              { name: "Night", start: "#232526", end: "#414345" },
                              { name: "Royal", start: "#8E2DE2", end: "#4A00E0" }
                            ].map((preset, idx) => (
                              <button
                                key={idx}
                                className="px-3 py-1 text-xs rounded-full border hover:scale-105 transition-transform"
                                style={{ background: `linear-gradient(90deg, ${preset.start}, ${preset.end})`, color: '#fff' }}
                                onClick={() => { setGradientStart(preset.start); setGradientEnd(preset.end); }}
                              >
                                {preset.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Additional Options */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Thumbnail Style</label>
                          <Select value={thumbnailStyle} onValueChange={setThumbnailStyle}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bold">Bold & Colorful</SelectItem>
                              <SelectItem value="minimal">Minimalist</SelectItem>
                              <SelectItem value="cinematic">Cinematic</SelectItem>
                              <SelectItem value="neon">Neon Glow</SelectItem>
                              <SelectItem value="retro">Retro/Vintage</SelectItem>
                              <SelectItem value="3d">3D Effect</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Headline (Optional)</label>
                          <Input
                            placeholder="Bold text overlay"
                            value={thumbnailHeadline}
                            onChange={(e) => setThumbnailHeadline(e.target.value)}
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <Button 
                    className="w-full bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600"
                    onClick={handleGenerateThumbnail}
                    disabled={loadingThumbnail}
                    data-testid="generate-thumbnail-btn"
                  >
                    {loadingThumbnail ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Thumbnail (30-60s)...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate {thumbnailTab === "pro" ? "Pro" : ""} Thumbnail with AI</>
                    )}
                  </Button>

                  {/* Generated Thumbnail */}
                  {generatedThumbnail ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="font-semibold">Generated Thumbnail</h3>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadThumbnail('png')}
                            data-testid="download-thumbnail-png-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            PNG
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadThumbnail('jpg')}
                            data-testid="download-thumbnail-jpg-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            JPG
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-center p-4 bg-gray-100 rounded-lg">
                        <img 
                          src={`data:image/png;base64,${generatedThumbnail.image_base64}`}
                          alt="Generated thumbnail"
                          className="aspect-video max-w-full object-contain rounded-lg shadow-lg"
                          data-testid="generated-thumbnail-image"
                        />
                      </div>
                      <p className="text-sm text-gray-500 text-center">1280 x 720 px • YouTube Ready</p>
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <div className="text-center text-white">
                        <Image className="w-16 h-16 mx-auto mb-3 opacity-50" />
                        <p className="font-bold text-xl">Thumbnail Preview</p>
                        <p className="text-sm opacity-75">1280 x 720 px</p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Brand Kit Manager */}
            {activeTool === "brand-kit" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Palette className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-gray-900">Brand Kit Manager</h2>
                      <Badge className="bg-violet-100 text-violet-700">
                        <Crown className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    </div>
                    <p className="text-gray-600">Manage all your brand assets in one place</p>
                  </div>
                </div>

                {!isLoggedIn && !userLoading && (
                  <LoginRequired message="Please login to use the Brand Kit Manager." />
                )}

                {isLoggedIn && !isPremium && (
                  <PremiumLock 
                    feature="Brand Kit Manager"
                    description="Store and manage your brand colors, fonts, logos, and guidelines. Upgrade to Pro to unlock."
                  />
                )}

                {isLoggedIn && isPremium && (
                  <div className="space-y-8">
                    {/* Brand Colors */}
                    <div>
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Droplet className="w-5 h-5 text-violet-600" />
                        Brand Colors
                      </h3>
                      <div className="flex gap-4 flex-wrap">
                        <div className="text-center">
                          <div className="w-20 h-20 bg-violet-600 rounded-lg shadow-md" />
                          <p className="text-sm mt-2 font-medium">Primary</p>
                          <p className="text-xs text-gray-500">#7C3AED</p>
                        </div>
                        <div className="text-center">
                          <div className="w-20 h-20 bg-purple-500 rounded-lg shadow-md" />
                          <p className="text-sm mt-2 font-medium">Secondary</p>
                          <p className="text-xs text-gray-500">#A855F7</p>
                        </div>
                        <div className="text-center">
                          <div className="w-20 h-20 bg-gray-900 rounded-lg shadow-md" />
                          <p className="text-sm mt-2 font-medium">Dark</p>
                          <p className="text-xs text-gray-500">#111827</p>
                        </div>
                        <Button variant="outline" className="w-20 h-20 border-dashed">
                          <Plus className="w-6 h-6 text-gray-400" />
                        </Button>
                      </div>
                    </div>

                    {/* Brand Fonts */}
                    <div>
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Type className="w-5 h-5 text-violet-600" />
                        Brand Typography
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 border rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Heading Font</p>
                          <p className="text-2xl font-bold">Inter Bold</p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Body Font</p>
                          <p className="text-lg">Inter Regular</p>
                        </div>
                      </div>
                    </div>

                    {/* Brand Logos */}
                    <div>
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Image className="w-5 h-5 text-violet-600" />
                        Logo Variations
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-6 border rounded-lg bg-white text-center">
                          <div className="w-full h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded flex items-center justify-center text-white font-bold">
                            LOGO
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Primary</p>
                        </div>
                        <div className="p-6 border rounded-lg bg-gray-900 text-center">
                          <div className="w-full h-16 bg-white rounded flex items-center justify-center text-gray-900 font-bold">
                            LOGO
                          </div>
                          <p className="text-xs text-gray-400 mt-2">Inverted</p>
                        </div>
                        <div className="p-6 border rounded-lg bg-white text-center">
                          <div className="w-16 h-16 bg-violet-600 rounded-full mx-auto flex items-center justify-center text-white font-bold text-xl">
                            L
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Icon</p>
                        </div>
                        <Button variant="outline" className="h-full min-h-[100px] border-dashed flex flex-col gap-2">
                          <Plus className="w-6 h-6 text-gray-400" />
                          <span className="text-sm text-gray-500">Upload Logo</span>
                        </Button>
                      </div>
                    </div>

                    <Button className="w-full bg-violet-600 hover:bg-violet-700">
                      <Download className="w-4 h-4 mr-2" />
                      Export Brand Kit (PDF)
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* Background Remover Tool */}
            {activeTool === "bg-remover" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center">
                    <Eraser className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Background Remover</h2>
                    <p className="text-gray-500">Remove backgrounds from images instantly</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-pink-500 to-rose-500 text-white">NEW</Badge>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Upload Section */}
                  <div className="space-y-4">
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-pink-400 transition-colors cursor-pointer"
                      onClick={() => document.getElementById('bg-remover-input').click()}
                    >
                      {bgRemoverPreview ? (
                        <img 
                          src={bgRemoverPreview} 
                          alt="Preview" 
                          className="max-h-64 mx-auto rounded-lg object-contain"
                        />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600 font-medium">Click to upload image</p>
                          <p className="text-sm text-gray-400 mt-1">PNG, JPG up to 10MB</p>
                        </>
                      )}
                      <input
                        id="bg-remover-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleBgRemoverUpload}
                        data-testid="bg-remover-upload"
                      />
                    </div>

                    {/* Background Color Options */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">New Background</label>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { value: "transparent", label: "Transparent", color: "bg-gray-100 border-2 border-dashed" },
                          { value: "#FFFFFF", label: "White", color: "bg-white border" },
                          { value: "#000000", label: "Black", color: "bg-black" },
                          { value: "#FF0000", label: "Red", color: "bg-red-500" },
                          { value: "#00FF00", label: "Green", color: "bg-green-500" },
                          { value: "#0000FF", label: "Blue", color: "bg-blue-500" }
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setBgColor(opt.value)}
                            className={`w-10 h-10 rounded-lg ${opt.color} ${bgColor === opt.value ? 'ring-2 ring-pink-500 ring-offset-2' : ''}`}
                            title={opt.label}
                            data-testid={`bg-color-${opt.value}`}
                          />
                        ))}
                        <input
                          type="color"
                          value={bgColor === "transparent" ? "#FFFFFF" : bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          className="w-10 h-10 rounded-lg cursor-pointer"
                          title="Custom color"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleRemoveBackground}
                      disabled={!bgRemoverImage || loadingBgRemover}
                      className="w-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700"
                      data-testid="remove-bg-btn"
                    >
                      {loadingBgRemover ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><Eraser className="w-4 h-4 mr-2" /> Remove Background</>
                      )}
                    </Button>
                  </div>

                  {/* Result Section */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700">Result</h3>
                    {bgRemoverResult ? (
                      <div className="space-y-4">
                        <div className="bg-gray-100 rounded-xl p-4 flex items-center justify-center min-h-[300px]"
                             style={{ backgroundImage: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAGElEQVQYV2NkIAIwEiGHYRSp1HBLHyMAG4AB/4D7COYAAAAASUVORK5CYII=")', backgroundRepeat: 'repeat' }}>
                          {bgRemoverResult.image_base64 ? (
                            <img 
                              src={`data:image/png;base64,${bgRemoverResult.image_base64}`}
                              alt="Background removed"
                              className="max-h-64 rounded-lg object-contain"
                              data-testid="bg-removed-result"
                            />
                          ) : (
                            <div className="text-center">
                              <Eraser className="w-12 h-12 text-pink-400 mx-auto mb-2" />
                              <p className="text-gray-500">Demo Preview</p>
                              <p className="text-xs text-gray-400">Full API integration coming soon</p>
                            </div>
                          )}
                        </div>
                        
                        {bgRemoverResult.image_base64 && (
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => downloadBgRemovedImage('png')} className="flex-1">
                              <Download className="w-4 h-4 mr-2" /> PNG
                            </Button>
                            <Button variant="outline" onClick={() => downloadBgRemovedImage('jpg')} className="flex-1">
                              <Download className="w-4 h-4 mr-2" /> JPG
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
                        <Eraser className="w-12 h-12 text-gray-300 mb-3" />
                        <p className="text-gray-500">Result will appear here</p>
                        <p className="text-sm text-gray-400 mt-1">Upload an image and click "Remove Background"</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* QR Code Generator */}
            {activeTool === "qr-generator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                    <Layout className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">QR Code Generator</h2>
                    <p className="text-gray-600">Create custom QR codes instantly</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-gray-700 to-gray-900 text-white">NEW</Badge>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Content Type</label>
                      <Select value={qrType} onValueChange={setQrType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="url">URL/Link</SelectItem>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Content</label>
                      <Input placeholder={qrType === 'url' ? 'https://example.com' : qrType === 'email' ? 'email@example.com' : 'Enter content'} value={qrContent} onChange={(e) => setQrContent(e.target.value)} data-testid="qr-content-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">QR Color</label>
                        <div className="flex gap-2">
                          <input type="color" value={qrColor} onChange={(e) => setQrColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                          <Input value={qrColor} onChange={(e) => setQrColor(e.target.value)} className="flex-1" />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Background</label>
                        <div className="flex gap-2">
                          <input type="color" value={qrBgColor} onChange={(e) => setQrBgColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                          <Input value={qrBgColor} onChange={(e) => setQrBgColor(e.target.value)} className="flex-1" />
                        </div>
                      </div>
                    </div>
                    <Button onClick={generateQrCode} className="w-full bg-gray-900 hover:bg-gray-800" data-testid="generate-qr-btn">
                      <Layout className="w-4 h-4 mr-2" /> Generate QR Code
                    </Button>
                  </div>
                  
                  <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl">
                    {generatedQr ? (
                      <div className="space-y-4 text-center">
                        <img src={generatedQr.dataUrl} alt="QR Code" className="w-48 h-48 mx-auto rounded-lg shadow-lg" data-testid="generated-qr" />
                        <Button onClick={downloadQrCode} variant="outline"><Download className="w-4 h-4 mr-2" /> Download PNG</Button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Layout className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">QR Code will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Mockup Generator */}
            {activeTool === "mockup-generator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Layers className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Mockup Generator</h2>
                    <p className="text-gray-600">Create product mockups instantly</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-cyan-500 to-blue-500 text-white">NEW</Badge>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Mockup Type</label>
                      <Select value={mockupType} onValueChange={setMockupType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="phone">Phone Screen</SelectItem>
                          <SelectItem value="laptop">Laptop Screen</SelectItem>
                          <SelectItem value="tablet">Tablet Screen</SelectItem>
                          <SelectItem value="tshirt">T-Shirt</SelectItem>
                          <SelectItem value="mug">Coffee Mug</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-cyan-400" onClick={() => document.getElementById('mockup-upload').click()}>
                      {mockupPreview ? (
                        <img src={mockupPreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">Upload your design</p>
                        </>
                      )}
                      <input id="mockup-upload" type="file" accept="image/*" className="hidden" onChange={handleMockupImageUpload} data-testid="mockup-upload" />
                    </div>
                    <Button onClick={handleGenerateMockup} disabled={loadingMockup || !mockupPreview} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600" data-testid="generate-mockup-btn">
                      {loadingMockup ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Layers className="w-4 h-4 mr-2" /> Generate Mockup</>}
                    </Button>
                  </div>
                  
                  <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl min-h-[300px]">
                    {generatedMockup ? (
                      <div className="text-center space-y-4">
                        <div className={`relative mx-auto ${mockupType === 'phone' ? 'w-32' : mockupType === 'laptop' ? 'w-64' : 'w-40'}`}>
                          <div className={`${mockupType === 'phone' ? 'aspect-[9/19] bg-gray-900 rounded-3xl p-2' : mockupType === 'laptop' ? 'aspect-video bg-gray-800 rounded-t-lg' : 'aspect-square bg-gray-700 rounded-lg'}`}>
                            <div className={`w-full h-full bg-white ${mockupType === 'phone' ? 'rounded-2xl' : 'rounded'} overflow-hidden`}>
                              <img src={generatedMockup.preview} alt="Mockup" className="w-full h-full object-cover" />
                            </div>
                          </div>
                          {mockupType === 'laptop' && <div className="h-3 bg-gray-700 rounded-b-lg"></div>}
                        </div>
                        <Badge className="bg-green-100 text-green-700">Mockup Ready!</Badge>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Layers className="w-16 h-16 text-cyan-300 mx-auto mb-3" />
                        <p className="text-gray-500">Mockup preview will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Placeholder for any unhandled tools */}
            {!["color-palette", "logo-maker", "social-templates", "banner-creator", "thumbnail-designer", "bg-remover", "qr-generator", "mockup-generator", "brand-kit"].includes(activeTool) && (
              <Card className="p-6 text-center">
                <div className="py-12">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {designTools.find(t => t.id === activeTool)?.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {designTools.find(t => t.id === activeTool)?.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {designTools.find(t => t.id === activeTool)?.description}
                  </p>
                  <Badge>Coming Soon</Badge>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="design" />
        </div>
      </div>
      <ToolSEOContent toolId="design-tools" />
      <Footer />
      <WhatsAppChat />
    </div>
  );
}
